import GatewayResult, { GatewayType } from "model/gameplay/records/results/gateway-result";
import { LLCustomLayers } from "names";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import ButtonElement from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import { Services } from "slotworks/services/services";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import Logger from "slotworks/utils/logger";
import { FadeIn, FadeOut, ZoomIn, Zoom } from "slotworks/graphics/animation/base-animations";
import Parallel from "slotworks/utils/contracts/parallel";
import Timer from "slotworks/utils/timer";
import PromptContent from "slotworks/components/prompt/prompt-content";
import Components from "slotworks/components/components";

class GatewayComponent {

    private layer: Layers;

    private introBurnAnimation: AnimatedSprite;
    private outroBurnAnimation: AnimatedSprite;
    private promptAnimation: AnimatedSprite;
    private gatewayIntroAnimation: AnimatedSprite;
    private gatewayAnimation: AnimatedSprite;
    private fortuneText: MultiStyleText;

    private promptWaitTime = 2500;
    private promptDelayTime = 500;
    private outroSoundDelayTime = 3300;

    private leftButton: ButtonElement;
    private rightButton: ButtonElement;

    private gatewayResult: GatewayResult;

    private vaultOpenSides = new Map<Side, AnimatedSprite>();

    public init() {

        this.layer = Layers.Custom.get(LLCustomLayers.VAULT_GATEWAY);
        this.layer.setScene("gateway").execute();

        this.introBurnAnimation = this.layer.getAnimatedSprite("pick_intro_burn");
        this.outroBurnAnimation = this.layer.getAnimatedSprite("pick_outro_burn");
        this.promptAnimation = this.layer.getAnimatedSprite("pick_outro_prompt");
        this.gatewayIntroAnimation = this.layer.getAnimatedSprite("pick_gateway_intro");
        this.gatewayAnimation = this.layer.getAnimatedSprite("pick_gateway");

        this.fortuneText = this.layer.getText("fortune_text");
        this.fortuneText.scale.x = 1.4;

        const leftAnimation = this.layer.getAnimatedSprite("pick_select_left");
        const rightAnimation = this.layer.getAnimatedSprite("pick_select_right");

        this.vaultOpenSides.set(Side.LEFT, leftAnimation);
        this.vaultOpenSides.set(Side.RIGHT, rightAnimation);

        this.leftButton = this.layer.getButton("hit_left");
        this.rightButton = this.layer.getButton("hit_right");

        this.hideAll();

        this.layer.container.visible = true;
    }

    public playAnimation(gatewayResult: GatewayResult) {
        this.init();

        Services.sound.customEvent("pick_music");
        Services.sound.customEvent("pick_intro_sfx");

        this.gatewayResult = gatewayResult;

        return new Sequence([
            () => this.playIntro(),
            () => this.getPlayerSelection(),
            (selection) => this.openSelectedGateway(selection, gatewayResult.gatewayType),
            () => this.playOutro()
        ]);
    }

    public playOutroFire() {
        this.init();
        return this.playOutro();
    }

    protected playOutro() {

        this.showAndPlay(this.outroBurnAnimation).execute();
        this.hideRevealAnimations();

        Timer.setTimeout(() => {
            this.outroBurnAnimation.visible = false;
            this.layer.container.alpha = 1;
            this.layer.container.visible = false;
        }, 2000);

        return Contract.getTimeoutContract(300);
    }

    private hideRevealAnimations() {
        this.promptAnimation.visible = false;
        Timer.setTimeout(() => {
            this.gatewayAnimation.visible = false;
            this.vaultOpenSides.forEach((animation) => animation.visible = false);
        }, 300);
    }

    private getPlayerSelection() {
        return new Contract((resolve) => {

            this.leftButton.visible = true;
            this.rightButton.visible = true;

            this.leftButton.once("pointerdown", () => {
                this.rightButton.removeAllListeners();
                this.leftButton.removeAllListeners();
                this.leftButton.visible = false;
                this.rightButton.visible = false;
                resolve(Side.LEFT);
            });

            this.rightButton.once("pointerdown", () => {
                this.rightButton.removeAllListeners();
                this.leftButton.removeAllListeners();
                this.leftButton.visible = false;
                this.rightButton.visible = false;
                resolve(Side.RIGHT);
            });
        });
    }

    private openSelectedGateway(selection: Side, gatewayType: GatewayType) {

        Services.sound.customEvent("pick");

        return new Sequence([
            () => Contract.wrap(() => FadeOut(this.fortuneText, 600).execute()),
            () => this.openVault(selection),
            () => new Parallel([
                () => Contract.wrap(() => {
                    this.gatewayIntroAnimation.visible = false;
                    this.gatewayAnimation.visible = false;
                    this.introBurnAnimation.visible = false;
                }),
                () => this.playGatewayTypeReveal(selection, gatewayType)
            ])
        ]);
    }

    private openVault(selection: Side) {
        this.showAndPlay(this.vaultOpenSides.get(selection)).execute();
        return Contract.getTimeoutContract(2500);
    }

    private playGatewayTypeReveal(side: Side, gatewayType: GatewayType) {
        switch (gatewayType) {
            case GatewayType.FREESPINS:
                return this.playFreespinAnimation(side);
            case GatewayType.MC_BONUS:
                return this.playMCAnimation(side);
            default:
                Logger.error("Invalid gateway requested.", gatewayType);
        }
    }

    private playFreespinAnimation(side: Side) {

        const freespinsWon = this.gatewayResult.freespinsWon;
        const promptContent = new PromptContent(["freespinswin"], this.promptWaitTime);
        promptContent.setText("value", freespinsWon.toString());

        // promptContent.setClickToSkip(true);

        return new Sequence([
            () => Contract.wrap(() => Services.sound.customEvent("pick_vault_spin")),
            () => new Parallel([
                () => Contract.getDelayedContract(this.promptDelayTime, () => Components.prompt.showPrompt(promptContent)),
                () => this.showAndPlay(this.promptAnimation),
                () => Contract.wrap(() => Timer.setTimeout(() => Services.sound.customEvent("pick_outro_sfx"), this.outroSoundDelayTime))
            ])
        ]);
    }

    private playMCAnimation(side: Side) {
        const promptContent = new PromptContent(["fortune_blast_won"], this.promptWaitTime);

        return new Parallel([
            () => Contract.wrap(() => Services.sound.customEvent("fortune_blast_music_intro")),
            () => Contract.getDelayedContract(this.promptDelayTime, () => Components.prompt.showPrompt(promptContent)),
            () => Contract.wrap(() => Timer.setTimeout(() => Services.sound.customEvent("pick_outro_sfx"), this.outroSoundDelayTime)),
            () => this.showAndPlay(this.promptAnimation)
        ]);
    }

    private showAndLoop(sprite: AnimatedSprite) {
        sprite.visible = true;
        sprite.currentFrame = 0;
        sprite.play();
    }

    private showAndPlay(sprite: AnimatedSprite) {
        sprite.visible = true;
        sprite.currentFrame = 0;
        return sprite.playOnce();
    }

    private playIntro() {

        this.fortuneText.visible = true;
        this.fortuneText.alpha = 0;

        return new Sequence([
            () => this.showAndPlay(this.introBurnAnimation),
            () => this.showAndPlay(this.gatewayIntroAnimation),
            () => Contract.wrap(() => FadeIn(this.fortuneText, 600).execute()),
            () => Contract.wrap(() => this.showAndLoop(this.gatewayAnimation)),
            () => Contract.wrap(() => Services.sound.customEvent("pick_intro_sfx2"))
        ]);
    }

    private hideAll() {
        const allSprites = [
            this.introBurnAnimation,
            this.leftButton,
            this.rightButton,
            this.outroBurnAnimation,
            this.gatewayIntroAnimation,
            this.gatewayAnimation,
            this.fortuneText,
            this.promptAnimation,
            this.vaultOpenSides.get(Side.LEFT),
            this.vaultOpenSides.get(Side.RIGHT)
        ];

        allSprites.forEach((sprite) => sprite.visible = false);
    }
}

enum Side {
    LEFT = "left",
    RIGHT = "right"
}

export default new GatewayComponent();
